<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { marked } from 'marked';
import { slugifyHeading } from '@/lib/learn-docs';
import { t } from '@/i18n';

const props = defineProps({
  source: {
    type: String,
    default: '',
  },
  continuation: {
    type: Boolean,
    default: false,
  },
});

marked.setOptions({
  gfm: true,
  breaks: true,
});

const articleRef = ref<HTMLElement | null>(null);
const html = ref('');
const teardownFns = ref<Array<() => void>>([]);

function clearEnhancements() {
  for (const teardown of teardownFns.value) {
    try {
      teardown();
    } catch {
      // noop
    }
  }

  teardownFns.value = [];
}

function preserveHtmlBlocks(source: string) {
  const lines = source.split('\n');
  let depth = 0;
  const out: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const opens = (line.match(/<div[\s>]/g) || []).length;
    const closes = (line.match(/<\/div>/g) || []).length;

    if (depth > 1 && line.trim() === '') {
      out.push('<!-- -->');
    } else {
      out.push(line);
    }

    depth += opens - closes;
    if (depth < 0) depth = 0;
  }

  return out.join('\n');
}

/** marked 有时会把块级 <img> 包进 <p>，导致样式异常或图片不显示 */
function unwrapBlockImages(htmlString: string) {
  return htmlString.replace(
    /<p>\s*(<(?:picture|img)[\s\S]*?<\/(?:picture|img)>|<img[^>]*\/?>)\s*<\/p>/gi,
    '$1'
  );
}

function ensureLazyImages() {
  const root = articleRef.value;
  if (!root) return;
  for (const img of root.querySelectorAll('img:not([loading])')) {
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
  }
}

function prepareMarkdownSource(source: string) {
  const trimmed = source.replace(/<!--\s*DOC_[A-Z0-9_ -]+\s*-->/g, '').trim();
  return trimmed;
}

function assignHeadingIds() {
  const root = articleRef.value;
  if (!root) return;

  root.querySelectorAll('h2, h3').forEach((element) => {
    const text = element.textContent?.trim();
    if (!text) return;
    element.id = slugifyHeading(text);
  });
}

async function copyPromptExample(box: Element, button: HTMLButtonElement) {
  const content = Array.from(box.childNodes)
    .filter(
      (node) =>
        !(node instanceof HTMLElement && node.classList.contains('doc-prompt-example__toolbar'))
    )
    .map((node) => ('textContent' in node ? node.textContent || '' : ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (!content) return;

  button.textContent = t('landingPages.learn.markdown.copying');
  await navigator.clipboard.writeText(content);
  button.textContent = t('landingPages.learn.markdown.copied');
  window.setTimeout(() => {
    button.textContent = t('landingPages.learn.markdown.copy_example');
  }, 1800);
}

function enhancePromptExamples() {
  const root = articleRef.value;
  if (!root) return;

  root.querySelectorAll('.doc-prompt-example').forEach((box) => {
    if (box.querySelector('.doc-prompt-example__toolbar')) return;

    const toolbar = document.createElement('div');
    toolbar.className = 'doc-prompt-example__toolbar';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'doc-prompt-copy-btn';
    button.textContent = t('landingPages.learn.markdown.copy_example');

    const onClick = () => {
      void copyPromptExample(box, button);
    };

    button.addEventListener('click', onClick);
    teardownFns.value.push(() => button.removeEventListener('click', onClick));

    toolbar.appendChild(button);
    box.insertBefore(toolbar, box.firstChild);
  });
}

watch(
  () => props.source,
  (source) => {
    if (!source) {
      html.value = '';
      return;
    }

    const prepared = prepareMarkdownSource(source);
    const parsed = marked.parse(preserveHtmlBlocks(prepared)) || '';
    html.value = unwrapBlockImages(parsed);
  },
  { immediate: true }
);

watch(html, async () => {
  clearEnhancements();
  await nextTick();
  assignHeadingIds();
  ensureLazyImages();
  enhancePromptExamples();
});

onBeforeUnmount(() => {
  clearEnhancements();
});

const hasContent = computed(() => Boolean(props.source?.trim()));
</script>

<template>
  <article
    v-if="hasContent"
    ref="articleRef"
    class="doc-prose"
    :class="{ 'doc-prose--continuation': continuation }"
    v-html="html"
  />
</template>
